import { useEffect, useId, useState, type KeyboardEvent, type MouseEvent } from "react";

import { IconButton } from "@similarweb/ui-components";

import { useSessionStorage } from "app/pages/activation/hooks/helpers/useSessionStorage";

import { ExpandablePanelVariant, type ExpandablePanelProps } from "./ExpandablePanel.types";
import * as SC from "./styles";

export const ExpandablePanel = ({
    title,
    children,
    className,
    variant = ExpandablePanelVariant.Default,
    dataAutomation = "activation-expandable-panel",
    defaultOpen = false,
    isOpen,
    onToggle,
    persistOpenState = false,
    persistenceKey,
}: ExpandablePanelProps) => {
    const isControlled = typeof isOpen === "boolean";
    const { getSessionStorage, setSessionStorage } = useSessionStorage();

    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
    const open = isControlled ? isOpen : uncontrolledOpen;

    const headerId = useId();
    const titleId = useId();
    const contentId = useId();

    const shouldPersist = Boolean(persistOpenState && persistenceKey && !isControlled);

    useEffect(() => {
        if (!shouldPersist || !persistenceKey) {
            return;
        }

        const stored = getSessionStorage<boolean | null>(persistenceKey, null);

        if (typeof stored === "boolean") {
            setUncontrolledOpen(stored);
        }
    }, [getSessionStorage, persistenceKey, shouldPersist]);

    const handleToggle = () => {
        const next = !open;

        if (!isControlled) {
            setUncontrolledOpen(next);

            if (shouldPersist && persistenceKey) {
                setSessionStorage(persistenceKey, next);
            }
        }

        onToggle?.(next);
    };

    const handleHeaderKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleToggle();
        }
    };

    const handleChevronClick = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        handleToggle();
    };

    return (
        <SC.Container
            className={className}
            data-automation={dataAutomation}
            data-expanded={open}
            $variant={variant}
        >
            <SC.Header
                id={headerId}
                role="button"
                tabIndex={0}
                aria-expanded={open}
                aria-controls={contentId}
                aria-labelledby={titleId}
                onClick={handleToggle}
                onKeyDown={handleHeaderKeyDown}
                $variant={variant}
                $isOpen={open}
            >
                {title}

                <SC.ChevronWrap $isOpen={open}>
                    <IconButton
                        type="flat"
                        tabIndex={-1}
                        aria-hidden
                        iconName="chev-right"
                        iconSize="xs"
                        dataAutomation="activation-expandable-panel-chevron"
                        onClick={handleChevronClick}
                    />
                </SC.ChevronWrap>
            </SC.Header>

            <SC.ContentWrapper
                id={contentId}
                role="region"
                aria-labelledby={headerId}
                $isOpen={open}
            >
                <SC.ContentInner $isOpen={open}>{children}</SC.ContentInner>
            </SC.ContentWrapper>
        </SC.Container>
    );
};
